import { Link, type LinkProps } from "@tanstack/react-router";
import type { UiLinkProps } from "@beweegbaas/ui";

/**
 * Maps the shared UI package's `UiLinkProps` onto TanStack's `Link`, so `<Link>` (link.tsx) and the
 * `Table` row-overlay links render real TanStack navigation instead of react-router's `NavLink`.
 */
export function TanStackUiLink({
  to,
  params,
  search,
  target,
  preventScrollReset,
  preload,
  discover: _discover, // react-router-specific; not applicable to TanStack Router
  className,
  tabIndex,
  "aria-hidden": ariaHidden,
  ref,
  children,
}: UiLinkProps) {
  // link.tsx passes a NavLink-style `className` function (which ignores active state); the table
  // overlay passes a plain string. Resolve both to a string for TanStack's `Link`.
  const resolvedClassName =
    className instanceof Function
      ? className({ isActive: false, isPending: false, isTransitioning: false })
      : className;

  // SAFETY: `@beweegbaas/ui` is router-agnostic, so it types `to`/`params`/`search` loosely; the
  // router validates them at the call site and this adapter only forwards them.
  const linkProps = {
    to,
    params,
    search,
    target,
    resetScroll: preventScrollReset ? false : undefined,
    preload,
    className: resolvedClassName,
    tabIndex,
    "aria-hidden": ariaHidden,
    ref,
    children,
  } as LinkProps;

  return <Link {...linkProps} />;
}
